import React, { useState } from 'react';
import styles from '../../../styles/LoginRegister.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import type { UserRegistrationDetails, AgencyRegistrationDetails } from '../../../types/Registration';
import { useAgencyRegistration } from '../../../hooks/useRegistration';

interface ReviewStepProps {
  userDetails: UserRegistrationDetails;
  agencyDetails: AgencyRegistrationDetails;
  onNext: () => void;
  onBack: () => void;
  onEdit: (step: number) => void;
  onUpdateUserDetails: (data: UserRegistrationDetails) => void;
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  userDetails,
  agencyDetails,
  onNext,
  onBack,
  onEdit,
}) => {
  const { registerAgency, isLoading, error } = useAgencyRegistration();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setSubmitError(null);
    const regResult = await registerAgency(userDetails, agencyDetails);

    if (regResult.success) {
      onNext(); // advances to the "check your email" step
    } else {
      setSubmitError(regResult.error || 'Registration failed. Please try again.');
    }
  };

  const InfoSection: React.FC<{ title: string; onEdit: () => void; children: React.ReactNode }> = ({
    title,
    onEdit,
    children
  }) => (
    <div className={styles.reviewInfoSection}>
      <div className={styles.reviewSectionHeader}>
        <h3 className={styles.reviewSectionTitle}>{title}</h3>
        <button
          type="button"
          onClick={onEdit}
          className={`${buttonStyles.button} ${buttonStyles.link} ${styles.reviewEditButton}`}
        >
          Edit
        </button>
      </div>
      {children}
    </div>
  );

  const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className={styles.reviewInfoRow}>
      <span className={styles.reviewInfoLabel}>{label}:</span>
      <span className={styles.reviewInfoValue}>{value}</span>
    </div>
  );

  return (
    <div className={styles.formSection}>
      <h2 className={styles.formTitle}>Review Your Information</h2>
      <p className={styles.formDescription}>
        Please review all your information before confirming. You can edit any section by clicking the "Edit" button.
      </p>

      <InfoSection title="User Details" onEdit={() => onEdit(0)}>
        <InfoRow label="Full Name" value={`${userDetails.fullNames} ${userDetails.surname}`} />
        {userDetails.nickname && <InfoRow label="Nickname" value={userDetails.nickname} />}
        <InfoRow label="Email" value={userDetails.email} />
        <InfoRow label="Mobile Number" value={userDetails.mobileNumber} />
        {userDetails.homeTelephone && <InfoRow label="Home Telephone" value={userDetails.homeTelephone} />}
        <InfoRow label="Date of Birth" value={userDetails.dateOfBirth} />
        <InfoRow label="Gender" value={userDetails.gender} />
        <InfoRow label="ID Type" value={userDetails.identificationType} />
        <InfoRow label="ID/Passport Number" value={userDetails.idNumber} />
        <InfoRow
          label="Address"
          value={[
            userDetails.unitNumber,
            userDetails.complex,
            userDetails.streetNumber,
            userDetails.streetName,
            userDetails.suburb,
            userDetails.city,
            userDetails.postalCode
          ].filter(Boolean).join(', ')}
        />
        {!userDetails.postalAddressSameAsResidential && (
          <InfoRow
            label="Postal Address"
            value={[
              userDetails.postalUnitNumber,
              userDetails.postalComplex,
              userDetails.postalStreetNumber,
              userDetails.postalStreetName,
              userDetails.postalSuburb,
              userDetails.postalCity,
              userDetails.postalPostalCode
            ].filter(Boolean).join(', ')}
          />
        )}
      </InfoSection>

      <InfoSection title="Agency Details" onEdit={() => onEdit(1)}>
        <InfoRow label="Company Name" value={agencyDetails.companyName} />
        <InfoRow label="Trading-As Name" value={agencyDetails.tradingAsName} />
        <InfoRow label="Registration Number" value={agencyDetails.companyRegistrationNumber} />
        <InfoRow
          label="Address"
          value={[
            agencyDetails.unitNumber,
            agencyDetails.complexName,
            agencyDetails.streetNumber,
            agencyDetails.streetName,
            agencyDetails.suburbName,
            agencyDetails.city,
            agencyDetails.postalCode
          ].filter(Boolean).join(', ')}
        />
      </InfoSection>

      <div className={styles.reviewWarningSection}>
        <h4 className={styles.reviewWarningTitle}>Important Information</h4>
        <ul className={styles.reviewWarningList}>
          <li>Your account will be created in an unverified state.</li>
          <li>You will be required to verify your email before full creation.</li>
          <li>After email verification, you will be able to choose between starting a trial or making payment directly for a full account.</li>
          <li>Before full creation, you will be required to choose a seat selection for your agency.</li>
        </ul>
      </div>

      {submitError && (
        <div className={styles.errorMessage} role="alert">
          {submitError}
        </div>
      )}

      <div className={styles.buttonGroupSpaced}>
        <button
          type="button"
          onClick={onBack}
          className={`${buttonStyles.button} ${buttonStyles.formSecondary}`}
          disabled={isLoading}
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          className={`${buttonStyles.button} ${buttonStyles.formPrimary}`}
          disabled={isLoading}
        >
          {isLoading ? 'Creating Account...' : 'Confirm'}
        </button>
      </div>
    </div>
  );
};

export default ReviewStep;